import type { ApiClient } from '@center-technologies/shared-api-clients';

export type SpherePermissionAction =
  | 'discover'
  | 'view'
  | 'edit'
  | 'delete'
  | 'manage_security';

export const COALESCE_WINDOW_MS = 16;
export const MAX_BATCH = 100;

export type PermissionStatus = 'loading' | 'resolved';

interface PermissionEntry {
  status: PermissionStatus;
  allowed: boolean;
}

/** `unavailable` = the check could not be answered (503, network); it is not a denial. */
export type PermissionOutcome = 'allowed' | 'denied' | 'unavailable';

interface PendingItem {
  sphereId: string;
  action: SpherePermissionAction;
  key: string;
  resolve: (value: PermissionOutcome) => void;
}

interface PendingBatch {
  items: Map<string, PendingItem>;
  permissionClient: ApiClient;
  timer: ReturnType<typeof setTimeout> | null;
}

interface PermissionsResponse {
  permissions: Record<string, Record<string, boolean>>;
}

// Cache: identityId::sphereId::action → entry
const byKey = new Map<string, PermissionEntry>();

// In-flight promises, same key structure
const promisesByKey = new Map<string, Promise<PermissionOutcome>>();

// Coalescer accumulator: identityId → PendingBatch
const pendingByIdentity = new Map<string, PendingBatch>();

function makeCacheKey(
  identityId: string,
  sphereId: string,
  action: SpherePermissionAction,
): string {
  return `${identityId}::${sphereId}::${action}`;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

async function flush(identityId: string): Promise<void> {
  const batch = pendingByIdentity.get(identityId);
  if (!batch) return;

  const { items, permissionClient } = batch;
  pendingByIdentity.delete(identityId);

  const itemList = Array.from(items.values());
  const chunks = chunkArray(itemList, MAX_BATCH);

  await Promise.all(
    chunks.map(async (chunk) => {
      const sphereIds = [...new Set(chunk.map((item) => item.sphereId))];
      const actions = [...new Set(chunk.map((item) => item.action as string))];

      try {
        const response = await (
          permissionClient as unknown as {
            post: <T>(path: string, data: unknown) => Promise<T>;
          }
        ).post<PermissionsResponse>('/spheres/permissions', { sphereIds, actions });

        const permissions = response?.permissions ?? {};

        for (const item of chunk) {
          const allowed = permissions[item.sphereId]?.[item.action] ?? false;
          // Clear in-flight before setting byKey so re-registers see 'resolved'
          promisesByKey.delete(item.key);
          byKey.set(item.key, { status: 'resolved', allowed });
          item.resolve(allowed ? 'allowed' : 'denied');
        }
      } catch {
        // The check could not be answered (permission-service 503, network): nothing was denied, so
        // nothing is cached — the next register asks again, and callers can tell an outage from a denial.
        for (const item of chunk) {
          promisesByKey.delete(item.key);
          byKey.delete(item.key);
          item.resolve('unavailable');
        }
      }
    }),
  );
}

/**
 * Register a (sphereId, action) pair for the identity's next coalesced batch.
 * Returns a Promise that resolves with the allowed boolean; an unanswerable
 * check fails closed to `false` (use `registerOutcome` to tell it from a denial).
 *
 * - Resolved cache hit → returns immediately; no new request.
 * - A failed check is not cached, so the next register for the key asks again.
 * - In-flight → returns the existing promise; no duplicate request.
 * - Cache miss → registers with the coalescer; fires after COALESCE_WINDOW_MS.
 *
 * Cache is keyed by identityId (not the ApiClient reference) so results survive
 * ApiClientsContext re-memoization and impersonation switches are self-invalidating.
 */
export function register(
  sphereId: string,
  action: SpherePermissionAction,
  identityId: string,
  permissionClient: ApiClient,
): Promise<boolean> {
  return registerOutcome(sphereId, action, identityId, permissionClient).then(
    (outcome) => outcome === 'allowed',
  );
}

/** Same coalescing and caching as `register`, but distinguishes `unavailable` from `denied`. */
export function registerOutcome(
  sphereId: string,
  action: SpherePermissionAction,
  identityId: string,
  permissionClient: ApiClient,
): Promise<PermissionOutcome> {
  const key = makeCacheKey(identityId, sphereId, action);

  const entry = byKey.get(key);
  if (entry?.status === 'resolved') {
    return Promise.resolve(entry.allowed ? 'allowed' : 'denied');
  }

  const inFlight = promisesByKey.get(key);
  if (inFlight) return inFlight;

  let resolvePromise!: (value: PermissionOutcome) => void;
  const promise = new Promise<PermissionOutcome>((res) => {
    resolvePromise = res;
  });
  promisesByKey.set(key, promise);
  byKey.set(key, { status: 'loading', allowed: false });

  if (!pendingByIdentity.has(identityId)) {
    pendingByIdentity.set(identityId, {
      items: new Map(),
      permissionClient,
      timer: null,
    });
  }

  const batch = pendingByIdentity.get(identityId)!;
  batch.items.set(key, { sphereId, action, key, resolve: resolvePromise });

  batch.timer ??= setTimeout(() => flush(identityId), COALESCE_WINDOW_MS);

  return promise;
}

/**
 * Clear all cached permission results, in-flight promises, and pending batches.
 * Call on logout and on impersonation start/end.
 */
export function resetSpherePermissionCache(): void {
  for (const batch of pendingByIdentity.values()) {
    if (batch.timer !== null) {
      clearTimeout(batch.timer);
    }
  }
  pendingByIdentity.clear();
  byKey.clear();
  promisesByKey.clear();
}
